'use client';

import React from 'react';
import Select, { SelectOption, SelectProps } from './Select';

interface SimpleSelectProps extends Omit<SelectProps<SelectOption, false>, 'value' | 'onChange' | 'options'> {
  value?: string;
  onChange?: (value: string) => void;
  options: Array<{ value: string; label: string }> | (() => Array<{ value: string; label: string }>);
}

/**
 * SimpleSelect - 一个简化的 Select 组件，用于处理简单的字符串值
 * 
 * 这个组件简化了 value 和 onChange 的处理：
 * - value: 接受字符串而不是对象
 * - onChange: 返回字符串而不是对象
 * - options: 可以是数组或返回数组的函数
 */
const SimpleSelect: React.FC<SimpleSelectProps> = ({
  value,
  onChange,
  options,
  ...props
}) => {
  // 处理 options - 如果是函数，调用它
  const optionsList = typeof options === 'function' ? options() : options;

  // 将字符串 value 转换为对象
  const selectedValue = React.useMemo(() => {
    if (!value) return null;
    return optionsList.find(opt => opt.value === value) || null;
  }, [value, optionsList]);

  // 处理 onChange，将对象转换为字符串
  const handleChange = React.useCallback((newValue: SelectOption | null) => {
    if (onChange) {
      onChange(newValue?.value || '');
    }
  }, [onChange]);

  return (
    <Select<SelectOption, false>
      {...props}
      value={selectedValue}
      onChange={handleChange}
      options={optionsList}
    />
  );
};

export default SimpleSelect;